/**
 * Tenant Detection Utilities
 *
 * Utilities for detecting tenant information from hostname and managing tenant context
 */

/**
 * Subdomain prefixes that serve the trader portal.
 * `trader.` is the canonical one; `trade.` is supported as an alias.
 */
export const TRADER_SUBDOMAIN_PREFIXES = ['trader.', 'trade.'] as const;

/**
 * Check whether a hostname (or host with port) is served by the trader portal.
 */
export function isTraderHost(host: string = window.location.hostname): boolean {
    return TRADER_SUBDOMAIN_PREFIXES.some(prefix => host.startsWith(prefix));
}

/**
 * Replace the leading trader subdomain (`trader.` or `trade.`) with another subdomain.
 * Returns the host unchanged when it is not a trader host.
 */
export function replaceTraderSubdomain(host: string, replacement: string): string {
    const prefix = TRADER_SUBDOMAIN_PREFIXES.find(candidate => host.startsWith(candidate));

    return prefix ? `${replacement}${host.slice(prefix.length)}` : host;
}

export interface TenantInfo {
    tenantId: string;
    tenantSlug: string;
    tenantName: string;
    systemName: string;
    customDomain?: string;
    isActive: boolean;
    isAdminTenant: boolean;
}

export interface TenantStats {
    customersCount: number;
    leadsCount: number;
    tradesCount: number;
    ordersCount: number;
    usersCount: number;
    managersCount: number;
}

/**
 * Extract tenant information from hostname
 */
export function detectTenantFromHostname(): {
    tenantSlug: string | null;
    isAdmin: boolean;
    isCustomDomain: boolean;
} {
    const hostname = window.location.hostname;

    // Check for admin subdomain
    if (hostname.startsWith('admin.')) {
        return {
            tenantSlug: 'admin',
            isAdmin: true,
            isCustomDomain: false,
        };
    }

    // Check for CRM subdomain pattern: crm.{tenant}.quantix.local
    const crmMatch = hostname.match(/^crm\.(.+)\.quantix\.local$/);
    if (crmMatch) {
        return {
            tenantSlug: crmMatch[1],
            isAdmin: false,
            isCustomDomain: false,
        };
    }

    // Check for Trader subdomain pattern: trader.{tenant}.quantix.local or trade.{tenant}.quantix.local
    const traderMatch = hostname.match(/^(?:trader|trade)\.(.+)\.quantix\.local$/);
    if (traderMatch) {
        return {
            tenantSlug: traderMatch[1],
            isAdmin: false,
            isCustomDomain: false,
        };
    }

    // Check for custom domain (not quantix.local)
    if (!hostname.endsWith('.quantix.local')) {
        return {
            tenantSlug: null,
            isAdmin: false,
            isCustomDomain: true,
        };
    }

    // Default fallback
    return {
        tenantSlug: null,
        isAdmin: false,
        isCustomDomain: false,
    };
}

/**
 * Get the current application type (crm, trader, admin)
 */
export function getApplicationType(): 'crm' | 'trader' | 'admin' {
    const hostname = window.location.hostname;

    if (hostname.startsWith('admin.')) {
        return 'admin';
    }

    if (hostname.startsWith('crm.')) {
        return 'crm';
    }

    if (isTraderHost(hostname)) {
        return 'trader';
    }

    // For custom domains, determine by path or other means
    const path = window.location.pathname;
    if (path.startsWith('/trader')) {
        return 'trader';
    }

    if (path.startsWith('/admin')) {
        return 'admin';
    }

    // Default to CRM for custom domains
    return 'crm';
}

/**
 * Generate URLs for different tenant applications
 */
export function generateTenantUrls(
    tenantSlug: string,
    customDomain?: string
): {
    crm: string;
    trader: string;
    admin: string;
} {
    if (customDomain) {
        return {
            crm: `https://crm.${customDomain}`,
            trader: `https://trader.${customDomain}`,
            admin: `https://admin.${customDomain}`,
        };
    }

    return {
        crm: `https://crm.${tenantSlug}.quantix.local`,
        trader: `https://trader.${tenantSlug}.quantix.local`,
        admin: `https://admin.quantix.local`,
    };
}

/**
 * Check if current user can manage tenants
 */
export function canManageTenants(user: any): boolean {
    return user?.is_tenant_manager === true;
}

/**
 * Check if current user is in admin tenant
 */
export function isAdminTenant(tenant: TenantInfo): boolean {
    return tenant.isAdminTenant;
}

/**
 * Validate tenant slug format
 */
export function validateTenantSlug(slug: string): boolean {
    // Tenant slug should be lowercase, alphanumeric with hyphens, 3-50 characters
    const regex = /^[a-z0-9-]{3,50}$/;
    return regex.test(slug) && !slug.startsWith('-') && !slug.endsWith('-');
}

/**
 * Format tenant slug from display name
 */
export function formatTenantSlug(displayName: string): string {
    return displayName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single
        .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Get tenant-specific API base URL
 */
export function getTenantApiBaseUrl(): string {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // For local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return `${protocol}//localhost:8000/api`;
    }

    // For production, use the same hostname but with API path
    return `${protocol}//${hostname}/api`;
}

/**
 * Get tenant-specific WebSocket URL
 */
export function getTenantWebSocketUrl(): string {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

    // For local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return `${protocol}//localhost:8000/ws`;
    }

    // For production, use the same hostname but with WebSocket path
    return `${protocol}//${hostname}/ws`;
}

/**
 * Tenant context storage keys
 */
export const TENANT_STORAGE_KEYS = {
    CURRENT_TENANT: 'quantix_current_tenant',
    TENANT_SWITCH_HISTORY: 'quantix_tenant_switch_history',
    TENANT_PREFERENCES: 'quantix_tenant_preferences',
} as const;

/**
 * Store tenant context in localStorage
 */
export function storeTenantContext(tenant: TenantInfo): void {
    try {
        localStorage.setItem(TENANT_STORAGE_KEYS.CURRENT_TENANT, JSON.stringify(tenant));
    } catch (error) {
        console.warn('Failed to store tenant context:', error);
    }
}

/**
 * Retrieve tenant context from localStorage
 */
export function getStoredTenantContext(): TenantInfo | null {
    try {
        const stored = localStorage.getItem(TENANT_STORAGE_KEYS.CURRENT_TENANT);
        return stored ? JSON.parse(stored) : null;
    } catch (error) {
        console.warn('Failed to retrieve tenant context:', error);
        return null;
    }
}

/**
 * Clear tenant context from localStorage
 */
export function clearTenantContext(): void {
    try {
        localStorage.removeItem(TENANT_STORAGE_KEYS.CURRENT_TENANT);
        localStorage.removeItem(TENANT_STORAGE_KEYS.TENANT_SWITCH_HISTORY);
    } catch (error) {
        console.warn('Failed to clear tenant context:', error);
    }
}
